import * as React from 'react';

import { cn } from '@/lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, id, ...props }, ref) => {
    return (
      <input
        id={id}
        data-testid={`${id}-testid`}
        type={type}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';

export interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label?: string;
  error?: string;
  requiredItem?: boolean;
  rightElement?: React.ReactNode;
}
export const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      className,
      type,
      id,
      label,
      onBlur,
      error,
      value,
      placeholder,
      requiredItem,
      rightElement,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [inputValue, setInputValue] = React.useState('');

    // handle the label shrink
    const isShrink = React.useMemo(
      () => isFocused || !!inputValue || !!value || placeholder,
      [isFocused, inputValue],
    );

    const handleInputChange = React.useCallback((element: HTMLInputElement) => {
      if (element) {
        const updateValue = () => setInputValue(element.value);
        element.addEventListener('input', updateValue);

        return () => {
          element.removeEventListener('input', updateValue);
        };
      }
    }, []);

    const inputValues = value === '' || value ? { value: value } : null;

    return (
      <div className='relative'>
        {label && (
          <label
            htmlFor={id}
            className={cn(
              'absolute left-3 pointer-events-none transition-all duration-200',
              isShrink ? 'text-[12px] top-[7px] text-[#5F5F5F]' : 'text-[15px] top-[17px] text-white',
            )}
          >
            {label}
            {requiredItem && <span className='text-[#DC1D00]'>*</span>}
          </label>
        )}
        <input
          id={id}
          data-testid={`${id}-testid`}
          type={type}
          aria-label={label}
          onFocus={() => setIsFocused(true)}
          defaultValue={inputValues ? undefined : ''}
          className={cn(
            'flex h-[60px] w-full rounded-md border border-input bg-transparent border-[#515764] px-3 py-2 pb-0 pt-[13px] text-sm text-white ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            className,
            error ? ' border-red  focus-visible:ring-red' : '',
          )}
          ref={(element) => {
            if (ref && typeof ref === 'function') {
              ref(element);
            }
            if (element && ref) {
              setInputValue(element.value);
              handleInputChange(element);
            }
          }}
          onBlur={(e) => {
            onBlur?.(e);
            setIsFocused(false);
          }}
          placeholder={placeholder}
          {...inputValues}
          {...props}
        />
        {rightElement && (
          <div className='absolute right-3 top-1/2 -translate-y-1/2'>{rightElement}</div>
        )}
        {
          <p
            aria-label='error'
            aria-errormessage={error}
            className='text-red text-xs absolute top-[63px]'
          >
            {error}
          </p>
        }
      </div>
    );
  },
);
CustomInput.displayName = 'CustomInput';
